<template>
  <ChartLayout title="Operational and planned capture capacity" units="%" half>
    <template #params>
      <ButtonGroup
        small
        :options="[
          { label: 'By sector', value: 'sector' },
          {
            label: 'By region',
            value: 'region',
            disabled: region !== 'World' || !!country,
          },
        ]"
        v-model="mode"
      />
    </template>
    <Chart :options="options" :key="`${mode}${region}${country || ''}`" />
    <template #notes>
      <p>
        Includes all operational, under construction and planned CO2 capture
        facilities with an announced capacity of more than 100 000 t per year or
        1000 t per year for direct air capture facilities and an announced
        timeline. Obtained by summing the estimated capacity of capture, full
        chain and CCU projects in the IEA CCUS Projects Database.
      </p>
    </template>
  </ChartLayout>
</template>

<script>
import { ButtonGroup } from "@international-energy-agency/vue-ui";
import ChartOptions, {
  ChartLayout,
} from "@international-energy-agency/vue-ui/highcharts";
import { Chart } from "highcharts-vue";
export default {
  components: {
    ButtonGroup,
    ChartLayout,
    Chart,
  },
  props: {
    projects: {
      type: Array,
      required: true,
    },
    region: {
      type: String,
      required: true,
    },
    country: {
      type: String,
      required: false,
    },
  },
  data() {
    return {
      mode: "sector",
      sectorOrder: [
        "Natural gas processing",
        "Hydrogen/ammonia",
        "Biofuels",
        "Other fuel transformation",
        "Power and heat",
        "Cement",
        "Iron and steel",
        "Chemicals",
        "Other industry",
        "DAC",
      ],
    };
  },
  watch: {
    region(region) {
      if (this.mode === "region" && region !== "World") {
        this.mode = "sector";
      }
    },
    country(country) {
      if (this.mode === "region" && country) {
        this.mode = "sector";
      }
    },
  },
  computed: {
    series() {
      const { mode } = this;
      return this.projects.reduce((series, project) => {
        // skip non-capture or projects coming online after 2030
        if (
          ["T&S", "Transport", "Storage", "Industrial cluster"].includes(
            project.projectType
          ) ||
          !project.year ||
          project.year > 2030
        )
          return series;
        const existing = series.find((series) => series.name === project[mode]);
        if (existing) {
          if (project.status === "Operational")
            existing.data[0] += project.capacity;
          existing.data[1] += project.capacity;
        } else {
          series.push({
            name: project[mode],
            data: [
              project.status === "Operational" ? project.capacity : 0,
              project.capacity,
            ],
          });
        }
        return series;
      }, []);
    },
    modeSort() {
      const { mode, sectorOrder } = this;
      return (a, b) =>
        mode === "sector"
          ? sectorOrder.indexOf(a.name) - sectorOrder.indexOf(b.name)
          : b.data[1] - a.data[1];
    },
    options() {
      const { series, modeSort } = this;
      return ChartOptions.buildOptions(
        {
          series: series.sort(modeSort),
          xAxis: {
            type: "category",
            categories: ["Operational in 2023", "2030"],
          },
          tooltip: { valueSuffix: " Mt CO2 per year" },
          plotOptions: { column: { stacking: "percent" } },
        },
        "column"
      );
    },
  },
};
</script>
